import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { functions as firebaseFunctions } from "../@shared/firebase/config";
import {
  NFT_WALLET_TARGETS,
  checkMetaMaskMainnet,
  connectNftWallet,
  getWalletConnectionMessage,
  shortenWalletAddress,
} from "../@shared/utils/nftWalletProviders";

const initialState = {
  status: "disconnected",
  address: "",
  chainId: "",
  error: "",
  errorCode: "",
  assets: [],
  scanStatus: "idle",
  limited: false,
};

const latencyBucket = (durationMs) => {
  if (durationMs < 500) return "under_500ms";
  if (durationMs < 2000) return "500ms_2s";
  if (durationMs < 5000) return "2s_5s";
  return "over_5s";
};

const eventCallable = () => firebaseFunctions.httpsCallable("recordWalletScanEvent");
const scanCallable = () => firebaseFunctions.httpsCallable("scanWalletNfts");

function NftMedia({ asset }) {
  const [failed, setFailed] = useState(false);
  if (!asset.mediaUri || failed) {
    return (
      <Box
        role="img"
        aria-label={`${asset.name || "NFT"} media unavailable`}
        sx={{
          width: "100%",
          aspectRatio: "1 / 1",
          display: "grid",
          placeItems: "center",
          borderRadius: 1,
          mb: 0.75,
          bgcolor: "rgba(255,255,255,0.05)",
          color: "#9aa0ff",
        }}
      >
        No preview
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={asset.mediaUri}
      alt={asset.name || "NFT media"}
      loading="lazy"
      onError={() => setFailed(true)}
      sx={{
        width: "100%",
        aspectRatio: "1 / 1",
        objectFit: "cover",
        borderRadius: 1,
        mb: 0.75,
      }}
    />
  );
}

export default function WalletScanCard({ providerKey }) {
  const target = NFT_WALLET_TARGETS[providerKey];
  const [state, setState] = useState(initialState);
  const providerRef = useRef(null);
  const userDisconnectingRef = useRef(false);

  const emitEvent = (eventName, details = {}) => {
    eventCallable()({
      eventName,
      provider: target.provider,
      network: target.network,
      ...details,
    }).catch(() => {});
  };

  useEffect(() => {
    const provider = providerRef.current;
    if (!provider?.on) return undefined;

    const handleDisconnect = () => {
      setState(initialState);
      if (!userDisconnectingRef.current) {
        eventCallable()({
          eventName: "wallet_disconnected",
          provider: target.provider,
          network: target.network,
        }).catch(() => {});
      }
    };
    const handleAccountsChanged = (accounts) => {
      const nextAddress = Array.isArray(accounts)
        ? accounts[0]
        : accounts?.publicKey?.toString?.() || accounts?.toString?.();
      if (!nextAddress) {
        handleDisconnect();
        return;
      }
      setState((current) => ({
        ...current,
        address: nextAddress,
        assets: [],
        scanStatus: "idle",
      }));
    };
    const handleChainChanged = (chainId) => {
      if (providerKey !== "metamask") return;
      const wrongNetwork = String(chainId || "").toLowerCase() !== "0x1";
      setState((current) => ({
        ...current,
        chainId,
        status: wrongNetwork ? "wrong-network" : "connected",
        error: wrongNetwork ? "Switch MetaMask to Ethereum Mainnet, then check again." : "",
        assets: [],
        scanStatus: "idle",
      }));
    };

    provider.on("disconnect", handleDisconnect);
    provider.on("accountsChanged", handleAccountsChanged);
    provider.on("accountChanged", handleAccountsChanged);
    provider.on("chainChanged", handleChainChanged);
    return () => {
      provider.removeListener?.("disconnect", handleDisconnect);
      provider.removeListener?.("accountsChanged", handleAccountsChanged);
      provider.removeListener?.("accountChanged", handleAccountsChanged);
      provider.removeListener?.("chainChanged", handleChainChanged);
    };
  }, [providerKey, state.status, target.network, target.provider]);

  const connect = async () => {
    setState({ ...initialState, status: "connecting" });
    emitEvent("wallet_connect_started");
    const result = await connectNftWallet(providerKey);
    providerRef.current = result.providerInstance || null;

    if (!result.ok) {
      const error = getWalletConnectionMessage(result.code, target.label);
      setState({ ...initialState, status: "error", error, errorCode: result.code });
      emitEvent("wallet_connect_failed", { errorCode: result.code });
      return;
    }

    const status = result.wrongNetwork ? "wrong-network" : "connected";
    setState({
      ...initialState,
      status,
      address: result.address,
      chainId: result.chainId,
      error: result.wrongNetwork
        ? "Switch MetaMask to Ethereum Mainnet, then check again."
        : "",
    });
    emitEvent("wallet_connect_succeeded");
  };

  const checkNetwork = async () => {
    try {
      const onMainnet = await checkMetaMaskMainnet(providerRef.current);
      setState((current) => ({
        ...current,
        status: onMainnet ? "connected" : "wrong-network",
        error: onMainnet
          ? ""
          : "MetaMask is still on the wrong network. Select Ethereum Mainnet and retry.",
      }));
    } catch (_) {
      setState((current) => ({
        ...current,
        error: "MetaMask network could not be checked. Open the wallet and retry.",
      }));
    }
  };

  const disconnect = async () => {
    userDisconnectingRef.current = true;
    try {
      if (providerKey === "phantom") await providerRef.current?.disconnect?.();
    } catch (_) {
      // Local session state is still cleared if the provider cannot disconnect.
    }
    providerRef.current = null;
    setState(initialState);
    emitEvent("wallet_disconnected");
    userDisconnectingRef.current = false;
  };

  const scan = async () => {
    const startedAt = Date.now();
    setState((current) => ({ ...current, scanStatus: "scanning", error: "", assets: [] }));
    emitEvent("nft_scan_started");
    try {
      const response = await scanCallable()({
        provider: target.provider,
        network: target.network,
        address: state.address,
      });
      const assets = Array.isArray(response?.data?.assets) ? response.data.assets : [];
      const scanStatus = assets.length ? "success" : "empty";
      setState((current) => ({
        ...current,
        assets,
        scanStatus,
        limited: Boolean(response?.data?.limited),
      }));
      emitEvent(assets.length ? "nft_scan_succeeded" : "nft_scan_empty", {
        latencyBucket: latencyBucket(Date.now() - startedAt),
        resultCategory: assets.length ? "results" : "empty",
      });
    } catch (error) {
      const errorCode = String(error?.details?.errorCode || error?.code || "scan_failed")
        .replace(/^functions\//, "")
        .slice(0, 64);
      setState((current) => ({
        ...current,
        scanStatus: "failed",
        errorCode,
        error: `${target.label} ${target.networkLabel} scan failed. Try again in a moment.`,
      }));
      emitEvent("nft_scan_failed", {
        errorCode,
        latencyBucket: latencyBucket(Date.now() - startedAt),
      });
    }
  };

  const connected = ["connected", "wrong-network"].includes(state.status);
  const busy = state.status === "connecting" || state.scanStatus === "scanning";

  return (
    <Box
      data-testid={`${providerKey}-scan-card`}
      sx={{
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.12)",
        p: 1.5,
        mb: 1.5,
        bgcolor: "rgba(0,0,0,0.3)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.25}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 40,
              height: 40,
              flex: "0 0 40px",
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontWeight: 900,
              background:
                providerKey === "phantom"
                  ? "linear-gradient(135deg, #ab9ff2, #5342a9)"
                  : "linear-gradient(135deg, #f6851b, #763d16)",
            }}
          >
            {providerKey === "phantom" ? "P" : "M"}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ color: "#e2d5ff", fontWeight: 700 }}>
              {target.label}
            </Typography>
            <Typography variant="caption" sx={{ color: "#9aa0ff" }}>
              {target.networkLabel} — NFT scanning only
            </Typography>
          </Box>
        </Stack>

        {!connected ? (
          <Button
            variant="contained"
            onClick={connect}
            disabled={busy}
            sx={{ bgcolor: "#6f52ed", fontWeight: 700, minHeight: 44 }}
          >
            {state.status === "connecting" ? "Connecting…" : "Connect"}
          </Button>
        ) : null}
      </Stack>

      <Box aria-live="polite" aria-atomic="true" sx={{ mt: 1.25 }}>
        {connected ? (
          <Typography variant="body2" sx={{ color: "#8ff0bf" }}>
            Connected · {state.status === "wrong-network" ? "Wrong network" : target.networkLabel} ·{" "}
            <span aria-label={`Full wallet address ${state.address}`} title={state.address}>
              {shortenWalletAddress(state.address)}
            </span>
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "#9aa0ff" }}>
            Disconnected
          </Typography>
        )}

        {state.error ? (
          <Typography role="alert" variant="body2" sx={{ color: "#ffb3c7", mt: 0.75 }}>
            {state.error}
          </Typography>
        ) : null}

        {state.scanStatus === "empty" ? (
          <Typography variant="body2" sx={{ color: "#ffd24d", mt: 0.75 }}>
            No supported NFTs were found for this {target.networkLabel} address. You can scan again.
          </Typography>
        ) : null}
      </Box>

      {connected ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.25 }}>
          {state.status === "wrong-network" ? (
            <Button variant="contained" onClick={checkNetwork} disabled={busy} sx={{ minHeight: 44 }}>
              Check network again
            </Button>
          ) : (
            <Button variant="contained" onClick={scan} disabled={busy} sx={{ minHeight: 44 }}>
              {state.scanStatus === "scanning" ? "Scanning…" : "Scan NFTs"}
            </Button>
          )}
          <Button variant="outlined" onClick={disconnect} disabled={state.status === "connecting"} sx={{ minHeight: 44 }}>
            Disconnect
          </Button>
        </Stack>
      ) : state.status === "error" ? (
        <Button variant="outlined" onClick={connect} sx={{ mt: 1.25, minHeight: 44 }}>
          Retry connection
        </Button>
      ) : null}

      {state.assets.length ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ color: "#e2d5ff", mb: 1, fontWeight: 700 }}>
            Scan results ({state.assets.length})
          </Typography>
          {state.limited ? (
            <Typography variant="caption" sx={{ color: "#ffd24d", display: "block", mb: 1 }}>
              Showing the first {state.assets.length} assets for this wallet.
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
              gap: 1,
            }}
          >
            {state.assets.map((asset) => (
              <Box
                key={asset.assetId}
                sx={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 1.5, p: 1, minWidth: 0 }}
              >
                <NftMedia asset={asset} />
                <Typography variant="caption" sx={{ color: "#e2d5ff", display: "block", fontWeight: 700, overflowWrap: "anywhere" }}>
                  {asset.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "#9aa0ff", display: "block", overflowWrap: "anywhere" }}>
                  {asset.collectionId || asset.contractAddress || "Unverified collection"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#9aa0ff", display: "block", overflowWrap: "anywhere" }}>
                  {asset.tokenStandard} · {asset.tokenId}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
